import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { storage, genId } from '../lib/storage';

const StudioContext = createContext(null);

const emptyProject = () => ({
  id: genId(),
  name: 'Untitled Project',
  createdAt: new Date().toISOString(),
  exactText: '',
  capitalization: '',
  applyScope: 'complete design',
  creationType: '', creationTypeLabel: '',
  purpose: '',
  topicCategory: '', topicCategoryLabel: '', topicItem: '', topicCustom: '',
  customConcept: '',
  fontFamily: '', fontFamilyLabel: '', fontStyle: '',
  fontStyleModifiers: [],
  // Creative & character-inspired reference (from creativeFonts.js), independent from named-font library
  creativeFont: null,
  // Structured custom font entry (name + description + weight/width/shape/baseline/etc.)
  customFont: {},
  // Font combo builder (hero / secondary / scriptAccent / smallSupporting)
  fontCombo: null,
  letterConstruction: '',
  layoutBasic: '', layoutStack: '', layoutArch: '', layoutPath: '', layoutShape: '', layoutPerspective: '', layoutDistortion: '',
  // Fill system (new): explicit mode governs which fill wins in the compiler.
  fillMode: 'Solid Color',
  solidColor: '#FF107A',
  solidOpacity: 100,
  colorMode: '',
  palette: '',
  gradient: { type: 'Linear', angle: 90, stops: [] },
  // Per-target fills — each target may hold its own { mode, color, opacity, gradient, paletteName }
  targetFills: {},
  colorTarget: 'Main Text Fill',
  customPalette: [],
  faceTreatment: '',
  patternFamily: '', patternSubtype: '', patternPlacement: [],
  materialFamily: '', materialSubtype: '', materialPlacement: [],
  finish: '',
  layers: [],
  internalDetails: [],
  perLetterOverrides: [],
  // Spacing controls (new)
  tracking: 0, wordSpacing: 0, lineSpacing: 0, stackGap: 0, controlledOverlap: false,
  // Custom lettering directives (new)
  lettering: {},
  // Character sheet PROMPT mode
  characterSheet: { enabled: false, chars: '' },
  // Holiday treatment id (looked up in holidayTreatments.js)
  holidayTreatment: '',
  supporting: [],
  lighting: '', camera: '', aspect: 'Square 1:1',
  background: '', backgroundLabel: '',
  mockupProduct: '', customBackground: '',
  canvasWidth: '', canvasHeight: '', canvasUnit: 'px',
  customEntries: {},
  negatives: [],
  subjects: [], uploads: [],
  composition: '', hierarchy: '', focalPoint: '', realism: '',
  intensity: {},
});

function reducer(state, action) {
  switch (action.type) {
    case 'SET': return { ...state, [action.key]: action.value };
    case 'PATCH': return { ...state, ...action.patch };
    case 'CUSTOM': return { ...state, customEntries: { ...state.customEntries, [action.key]: action.value } };
    case 'TOGGLE_ARR': {
      const cur = state[action.key] || [];
      const exists = cur.includes(action.value);
      return { ...state, [action.key]: exists ? cur.filter((x) => x !== action.value) : [...cur, action.value] };
    }
    case 'RESET': return emptyProject();
    case 'LOAD': return { ...emptyProject(), ...action.project };
    default: return state;
  }
}

const HIST_LIMIT = 40;

export function StudioProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, null, () => {
    const cur = storage.getCurrent();
    return cur ? { ...emptyProject(), ...cur } : emptyProject();
  });
  const undoStack = React.useRef([]);
  const redoStack = React.useRef([]);
  const lastSnapshot = React.useRef(null);
  useEffect(() => { storage.setCurrent(state); }, [state]);

  const dispatchWithHistory = React.useCallback((action) => {
    if (action.type !== 'UNDO' && action.type !== 'REDO') {
      undoStack.current.push(lastSnapshot.current || state);
      if (undoStack.current.length > HIST_LIMIT) undoStack.current.shift();
      redoStack.current = [];
    }
    if (action.type === 'UNDO') {
      const prev = undoStack.current.pop();
      if (prev) { redoStack.current.push(state); dispatch({ type: 'LOAD', project: prev }); }
      return;
    }
    if (action.type === 'REDO') {
      const nxt = redoStack.current.pop();
      if (nxt) { undoStack.current.push(state); dispatch({ type: 'LOAD', project: nxt }); }
      return;
    }
    lastSnapshot.current = state;
    dispatch(action);
  }, [state]);

  return <StudioContext.Provider value={{ state, dispatch: dispatchWithHistory }}>{children}</StudioContext.Provider>;
}

export const useStudio = () => useContext(StudioContext);
